import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import type { Article } from "@/types/blog";

interface ArticleCardProps {
  article: Article;
}

const AUTHOR_NAME: Record<Article["frontmatter"]["author"], string> = {
  pinch: "Pinch Guigui",
  ezra: "Ezra Rothchild",
};

export function ArticleCard({ article }: ArticleCardProps) {
  const { t, i18n } = useTranslation();
  const fm = article.frontmatter;
  const dateLabel = new Date(fm.publishedAt).toLocaleDateString(
    i18n.language === "he" ? "he-IL" : "en-US",
    { year: "numeric", month: "short", day: "numeric" },
  );

  return (
    <Link
      to={`/blog/${fm.slug}`}
      style={{
        display: "flex",
        flexDirection: "column",
        background: "var(--dyna-bg-elevated)",
        border: "1px solid var(--dyna-border)",
        borderRadius: 16,
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        transition: "transform 240ms ease, border-color 240ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = "var(--lavender-400)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "var(--dyna-border)";
      }}
    >
      <div
        style={{
          aspectRatio: "16 / 9",
          background: fm.coverImage
            ? `url(${fm.coverImage}) center/cover no-repeat`
            : "linear-gradient(135deg, rgba(90,70,102,0.5), rgba(156,139,194,0.2))",
          borderBottom: "1px solid var(--dyna-border)",
        }}
      />
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {fm.tags.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {fm.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "var(--lavender-400)",
                  background: "rgba(156,139,194,0.10)",
                  padding: "3px 10px",
                  borderRadius: 999,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <h3
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 20,
            lineHeight: 1.3,
            fontWeight: 600,
            color: "var(--dyna-fg)",
            margin: 0,
          }}
        >
          {fm.title}
        </h3>
        <p
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 14,
            lineHeight: 1.55,
            color: "var(--dyna-fg-muted)",
            margin: 0,
            flex: 1,
          }}
        >
          {fm.excerpt}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: "var(--dyna-fg-muted)",
            paddingTop: 8,
            borderTop: "1px solid var(--dyna-border)",
          }}
        >
          <span>{AUTHOR_NAME[fm.author]}</span>
          <span>·</span>
          <span>{dateLabel}</span>
          <span style={{ marginInlineStart: "auto", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Clock size={11} />
            {t("pages.blog.readingTime", { minutes: article.readingTime })}
          </span>
        </div>
      </div>
    </Link>
  );
}
